import { useState } from "react";
import { Minus, Plus, X } from "lucide-react";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import type { ProductAllocation } from "@/marketing/models/influencerCampaign";

import { clampChoiceCount } from "./productChoice";

interface ProductPoolChoiceProps {
  allocations: ProductAllocation[];
  onAllocationsChange: (next: ProductAllocation[]) => void;
  // productChoiceCount is kept as a string to mirror cardForm.productChoiceCount (blank = "all", fixed).
  choiceCount: string;
  onChoiceCountChange: (next: string) => void;
}

export function ProductPoolChoice({
  allocations,
  onAllocationsChange,
  choiceCount,
  onChoiceCountChange,
}: ProductPoolChoiceProps) {
  const [skuDraft, setSkuDraft] = useState("");
  const [qtyDraft, setQtyDraft] = useState("");

  const poolSize = allocations.length;
  const parsedChoice = Number(choiceCount.trim());
  const choice =
    choiceCount.trim() && Number.isFinite(parsedChoice) ? clampChoiceCount(parsedChoice, poolSize) : 0;

  const addSku = () => {
    const sku = skuDraft.trim();
    const qty = Number(qtyDraft);
    if (!sku || allocations.some((a) => a.sku === sku)) return;
    if (!Number.isInteger(qty) || qty < 1 || qty > 100) return;
    onAllocationsChange([...allocations, { sku, quantity: qty }]);
    setSkuDraft("");
    setQtyDraft("");
  };

  const removeSku = (sku: string) => {
    const nextPool = allocations.filter((a) => a.sku !== sku);
    onAllocationsChange(nextPool);
    // Keep choice within the shrunk pool (auto-decrease when it exceeds the new pool size).
    const reclamped = clampChoiceCount(choice, nextPool.length);
    if (String(reclamped) !== choiceCount.trim() && choice > nextPool.length) {
      onChoiceCountChange(String(reclamped));
    }
  };

  const step = (delta: number) => {
    const next = clampChoiceCount(choice + delta, poolSize);
    if (next === choice) return;
    onChoiceCountChange(String(next));
  };

  // Mirror addSku's guards so the button never looks clickable for a silent no-op.
  const qtyNum = Number(qtyDraft);
  const addDisabled =
    !skuDraft.trim() ||
    allocations.some((a) => a.sku === skuDraft.trim()) ||
    !Number.isInteger(qtyNum) ||
    qtyNum < 1 ||
    qtyNum > 100;

  return (
    <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-[1fr_128px] md:tw-items-stretch">
      <div className="tw-flex tw-flex-col tw-gap-1.5">
        {allocations.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {allocations.map((allocation) => (
              <div
                key={allocation.sku}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1.5 tw-text-sm"
              >
                <span className="tw-flex-1 tw-truncate" title={allocation.sku}>
                  {allocation.sku}
                </span>
                <span className="tw-text-xs tw-text-muted-foreground">
                  × {allocation.quantity}
                </span>
                <button
                  type="button"
                  onClick={() => removeSku(allocation.sku)}
                  className="tw-text-muted-foreground hover:tw-text-foreground"
                  aria-label={`Remove ${allocation.sku}`}
                >
                  <X className="tw-h-3.5 tw-w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="tw-flex tw-gap-2">
          <Input
            value={skuDraft}
            onChange={(e) => setSkuDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addSku();
              }
            }}
            placeholder="SKU (e.g. REB-CR-30ML)"
            className="tw-flex-1"
          />
          <Input
            type="number"
            min={1}
            max={100}
            value={qtyDraft}
            onChange={(e) => setQtyDraft(e.target.value)}
            placeholder="Qty"
            className="tw-w-20"
          />
          <Button
            type="button"
            variant="outline"
            onClick={addSku}
            disabled={addDisabled}
          >
            Add
          </Button>
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-border tw-border-primary/40 tw-bg-primary/5 tw-p-2 tw-text-center">
        <div className="tw-text-xl tw-font-extrabold tw-leading-none tw-text-primary">
          {choice === 0 ? "all" : choice}
        </div>
        <div className="tw-text-[10px] tw-font-semibold tw-text-foreground/70">
          creator picks
        </div>
        <div className="tw-inline-flex tw-items-center tw-overflow-hidden tw-rounded-md tw-border tw-border-primary/40">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Decrease creator choice count"
            className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-text-primary hover:tw-bg-primary/10"
          >
            <Minus className="tw-h-3 tw-w-3" />
          </button>
          <span className="tw-w-7 tw-text-center tw-text-xs tw-font-bold">{choice}</span>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Increase creator choice count"
            className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-text-primary hover:tw-bg-primary/10"
          >
            <Plus className="tw-h-3 tw-w-3" />
          </button>
        </div>
        <div className="tw-text-[10px] tw-text-muted-foreground">
          of {poolSize} in pool
        </div>
      </div>
    </div>
  );
}
